const cache = new Map();

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

export function getCached(key) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt) {
    cache.delete(key);
    return null;
  }
  return entry.data;
}

export function setCache(key, data, ttl = DEFAULT_TTL) {
  cache.set(key, { data, expiresAt: Date.now() + ttl });
}

export function clearCache(keyPrefix) {
  if (!keyPrefix) {
    cache.clear();
    return;
  }
  for (const key of cache.keys()) {
    if (key.startsWith(keyPrefix)) cache.delete(key);
  }
}

/**
 * Stale-while-revalidate fetch.
 * Returns cached data immediately if available (even if stale),
 * then revalidates in the background.
 */
export async function cachedFetch(url, options = {}) {
  const { ttl = DEFAULT_TTL, forceRefresh = false, ...fetchOptions } = options;
  const cacheKey = url;

  // Return fresh cache immediately
  if (!forceRefresh) {
    const cached = getCached(cacheKey);
    if (cached) return cached;
  }

  // Check for stale entry (expired but still in map)
  const staleEntry = cache.get(cacheKey);

  // If stale data exists and not force-refreshing, return stale and revalidate in background
  if (staleEntry && !forceRefresh) {
    // Fire-and-forget revalidation
    doFetch(url, fetchOptions, cacheKey, ttl).catch(() => {});
    return staleEntry.data;
  }

  // No cached data at all — must fetch
  return doFetch(url, fetchOptions, cacheKey, ttl);
}

async function doFetch(url, fetchOptions, cacheKey, ttl) {
  const token = localStorage.getItem('token');
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...fetchOptions.headers,
  };

  const res = await fetch(url, { ...fetchOptions, headers });
  if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
  const data = await res.json();
  setCache(cacheKey, data, ttl);
  return data;
}
